import * as _ from "lodash";
import * as React from "react";
import { toast } from "react-toastify";

import { Button } from "src/library/components/Button";
import { ITerminology } from "src/library/utils/string-manipulations/tokenize-string";
import {
  OrderAdminNoteModal,
  OrderAdminHeader
} from "src/core/orders/components/Modals";
import { useOrder } from "src/core/orders/hooks";
import { getAllowOrderCustomerComments } from "src/core/drops/utils/inherited-settings";
import { Text } from "src/library/components/Text";
import { useCurrentUser } from "src/core/users/hooks";

import {
  AddOrderNoteParams,
  orderNotesApi
} from "../api/order-notes.api";
import {
  IOrderNote,
  OrderNoteSourceEnum
} from "../api/orders.api";

interface IProps {
  orderId: number;
  terminology: ITerminology;
}

const OrderNoteButton: React.ReactFCWithChildren<IProps> = ({ orderId, terminology }) => {
  const [ orderNoteModal, setOrderNoteModal ] = React.useState<{ visible: boolean; note?: IOrderNote }>({ visible: false });
  const [ addOrderNoteResponse, addOrderNoteRequest ] = orderNotesApi("ADD_ORDER_NOTE");
  const [ { currentRole, currentUser } ] = useCurrentUser();

  const [
    {
      currentOrder, isLoading: isLoadingOrder, isSubmittingOrder, isSavingOrder
    },
    { refreshOrder }
  ] = useOrder(
    orderId
  );

  const allowOrderCustomerCommentSettings = currentOrder.data?.branch?.groupedSettings ? getAllowOrderCustomerComments(currentOrder.data?.branch?.groupedSettings) : undefined;

  // handle note add response
  React.useEffect(() => {
    if (addOrderNoteResponse.data) {
      toast.success("Order note created!");
      refreshOrder();
    } else if (addOrderNoteResponse.error) {
      toast.error(`Could not create order note - ${addOrderNoteResponse.error.message}`);
    }
  }, [ addOrderNoteResponse ]);

  const orderNoteSource = React.useMemo(() => {
    // set to customer if role is customer, otherwise return as staff
    if (currentRole?.type === "customer") {
      return OrderNoteSourceEnum.CUSTOMER;
    }

    return OrderNoteSourceEnum.STAFF;
  }, [ currentRole ]);

  // Add an order note
  const addOrderNote = React.useCallback(
    (body: string) => {
      if (currentUser) {
        const note: IOrderNote = {
          body,
          source: orderNoteSource,
          authorId: currentUser.id,
          orderId
        };

        addOrderNoteRequest<AddOrderNoteParams>({
          note,
          orderId
        });
      }
    },
    [
      orderId,
      currentUser,
      orderNoteSource,
      addOrderNoteRequest
    ]
  );

  if (currentOrder.data && currentOrder.mode === "view" && currentRole && (currentRole.type === "admin" || currentRole.type === "customer" && allowOrderCustomerCommentSettings?.allowed)) {
    return (
      <>
        <OrderAdminHeader
          PrimaryActionComponent={(
            <Button
              $buttonType="primary"
              onClick={() => setOrderNoteModal({
                visible: true,
                note: {
                  body: "",
                  orderId: 0,
                  authorId: 0,
                  source: orderNoteSource
                }
              })}
              disabled={isLoadingOrder || isSavingOrder || isSubmittingOrder}
            >
              {`Leave a note on this ${terminology.order.singular.text}`}
            </Button>
          )}
        />

        <OrderAdminNoteModal
          show={orderNoteModal.visible}
          noteObj={orderNoteModal.note}
          onConfirm={addOrderNote}
          handleDismiss={() => setOrderNoteModal({ visible: false })}
        />
      </>
    );
  }

  /**
  * order lines have not been saved to the order yet, so leaving a note will essentially wipe the orderlines from cache.
  * so here at the point of editing we stop the user from adding a note.
  */
  if (currentOrder.data && currentOrder.mode === "edit" && currentRole && (currentRole.type === "admin" || currentRole.type === "customer" && allowOrderCustomerCommentSettings?.allowed)) {
    return (
      <Text fontSize="h3">
        Place the order or save it as a draft to leave a note.
      </Text>
    );
  }

  return null;
};

export default OrderNoteButton;
